import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEO from '@/components/feature/SEO';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const coreValues = [
  {
    icon: 'ri-focus-2-line',
    title: '心无旁骛',
    description: '在纷繁世界中守住内心的专注，不被外界杂音干扰，将全部心力倾注于真正重要之事。',
  },
  {
    icon: 'ri-flag-2-line',
    title: '专注目标',
    description: '明确方向，设定清晰的目标路径，以坚定的意志力持续前行，不为诱惑所动。',
  },
  {
    icon: 'ri-footprint-line',
    title: '走稳每一步',
    description: '不急于求成，不浮躁冒进。每一步都走得扎实、走得从容，积跬步以至千里。',
  },
  {
    icon: 'ri-team-line',
    title: '志同道合',
    description: '与同频者同行，在相互激励中成长，在分享交流中升华，共同践行专一之道。',
  },
];

const featuredArticles = articles.slice(0, 3);

export default function Home() {
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '一本道',
    url: 'https://yibendao.cn',
    description: '一本道，专注于正道、专一、坚定前行的人生成长理念。加入一本道社区，与志同道合者共同践行专一之道，实现人生跃升。',
    inLanguage: 'zh-CN',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://yibendao.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '一本道 · 专注正道人生成长平台',
    description: '一本道，专注于正道、专一、坚定前行的人生成长理念。加入一本道社区，与志同道合者共同践行专一之道。',
    url: 'https://yibendao.cn',
    isPartOf: {
      '@type': 'WebSite',
      name: '一本道',
      url: 'https://yibendao.cn',
    },
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <ScrollToTop />
      <SEO
        title="一本道·专注正道人生成长平台 - 心无旁骛·专注目标·走稳每一步"
        description="一本道，专注于正道、专一、坚定前行的人生成长理念。探索一本道的核心价值：心无旁骛、专注目标、走稳人生每一步。加入一本道社区，与志同道合者共同践行专一之道，实现人生跃升。"
        keywords="一本道,一本道成长理念,一本道人生哲学,一本道专注之道,一本道目标管理,走一本道,一本道社区"
        canonicalUrl="https://yibendao.cn"
        ogType="website"
      />
      <SchemaOrg schema={websiteSchema} />
      <SchemaOrg schema={webpageSchema} />

      <Navbar />

      <main>
        <section className="relative w-full h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=A%20serene%20minimalist%20ink%20wash%20painting%20style%20landscape%20with%20layered%20misty%20mountains%20in%20warm%20earth%20tones%20soft%20golden%20morning%20light%20ethereal%20fog%20ancient%20pine%20tree%20silhouette%20zen%20Chinese%20aesthetic%20abstract%20artistic%20composition%20high%20detail%20textured&width=1800&height=900&seq=hero-bg-01&orientation=landscape"
            alt="一本道 · 专注正道人生成长平台"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/40"></div>

          <div className="relative z-10 w-full text-center px-4 md:px-6">
            <h1 className="font-display text-3xl md:text-5xl lg:text-6xl font-semibold text-background-50 drop-shadow-lg leading-tight">
              走一条<strong className="font-bold">正道</strong>
            </h1>
            <p className="mt-6 text-base md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed drop-shadow-md">
              心无旁骛，专注目标，走稳人生每一步。<br className="hidden sm:block" />
              在纷繁世界中守住本心，践行专一之道，实现人生跃升。
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/zhuanzhu-zhidao"
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                探索专注之道
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-background-50/20 backdrop-blur-sm text-background-50 border border-background-50/30 px-8 py-3.5 rounded-md text-sm font-medium hover:bg-background-50/30 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                了解一本道
              </Link>
            </div>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce">
            <i className="ri-arrow-down-s-line text-background-50/70 text-2xl"></i>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900">
                探索内容分类
              </h2>
              <p className="mt-3 text-sm text-foreground-500 max-w-xl mx-auto">
                围绕一本道的核心理念，我们精心整理了四大内容板块，助你在不同维度深入修习专一之道。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  className="group bg-background-50 rounded-lg p-6 hover:bg-background-50 transition-colors duration-200 border border-background-200/70 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-4 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors duration-200">
                    <i className={`${category.icon} text-xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                    {category.name}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed mb-3">
                    {category.description}
                  </p>
                  <span className="text-xs text-foreground-400">
                    {category.articleCount} 篇文章
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900">
                精选文章
              </h2>
              <p className="mt-3 text-sm text-foreground-500 max-w-xl mx-auto">
                从专注之道到人生哲学，每一篇文章都是一次深度的思考和真诚的分享。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredArticles.map((article) => {
                const category = categories.find((c) => c.slug === article.category);
                return (
                  <article key={article.slug} className="group cursor-pointer">
                    <Link to={`/article/${article.slug}`} className="block">
                      <div className="relative w-full h-48 rounded-lg overflow-hidden mb-4">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="flex items-center gap-3 mb-2">
                        {category && (
                          <span className="text-xs font-medium text-primary-600 bg-primary-100/70 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                            {category.name}
                          </span>
                        )}
                        <span className="text-xs text-foreground-400">{article.readTime} 分钟阅读</span>
                      </div>
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 leading-snug mb-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-500 leading-relaxed line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center gap-2 mt-3">
                        <img
                          src={article.authorAvatar}
                          alt={article.author}
                          className="w-6 h-6 rounded-full object-cover"
                        />
                        <span className="text-xs text-foreground-400">{article.author} · {article.publishedAt}</span>
                      </div>
                    </Link>
                  </article>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/category/zhuanzhu-zhidao"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                浏览全部文章
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-14">
              <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground-900">
                一本道的核心理念
              </h2>
              <p className="mt-3 text-sm text-foreground-500 max-w-xl mx-auto">
                "一"为专一，"本"为根本，"道"为道路。坚守一条正道，走稳每一步人生路。
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {coreValues.map((value, index) => (
                <div
                  key={index}
                  className="text-center p-6 rounded-lg bg-background-50 border border-background-200/70"
                >
                  <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mb-4">
                    <i className={`${value.icon} text-2xl`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">
                    {value.title}
                  </h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-accent-500">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-background-50">
              加入一本道社区
            </h2>
            <p className="mt-4 text-sm md:text-base text-background-200 max-w-2xl mx-auto leading-relaxed">
              与志同道合者同行，在相互激励中成长，在分享交流中升华。
              一起践行专一之道，实现人生的每一次跃升。
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/category/xinlu-licheng"
                className="inline-flex items-center gap-2 bg-background-50 text-accent-600 px-8 py-3.5 rounded-md text-sm font-medium hover:bg-background-100 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                阅读心路历程
                <i className="ri-arrow-right-line"></i>
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 bg-accent-600 text-background-50 border border-background-50/30 px-8 py-3.5 rounded-md text-sm font-medium hover:bg-accent-700 transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}